import React, {useCallback, useEffect, useMemo} from "react";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import useAccessLevels from "../../../hooks/use-access-levels";
import ToastService from "../../../../core/services/toast";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from "../../form";
import Button from "../../base/button/button";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {useAppDispatch} from "../../../redux";
import Api from "../../../../core/services/api/api";
import {useOperatorInformation} from "../../../hooks/use-operator-information";
import {usePartnerInformation} from "../../../hooks/use-partner-information";

const EditProductPartNumberDialog = ({
                                         open,
                                         onSuccess,
                                         close,
                                         partId,
                                         partNo,
                                     }) => {
    const operatorAccessLevels = useAccessLevels();
    const [submitting, setSubmitting] = React.useState(false);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();
    const operatorInformation = useOperatorInformation();
    const partnerInformation = usePartnerInformation();

    const schema = useMemo(() => Yup.object().shape({
        partNo: Yup.string().trim().nullable().required(ValidateMessages.required)
            .test("must-be-different", `The new part number must be different from the current one: ${partNo}`, function (value) {
                if (!value || !partNo) {
                    return true; // If either value is empty, skip this validation (handled by 'required' rule)
                }
                return value?.toLowerCase()?.trim() !== partNo.toLowerCase().trim();
            }),
    }), [partNo]);

    const initialValues = useMemo(() => ({
        partNo: partNo ?? "",
    }), [partNo]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Warns the user when the main API is used in the development environment.
     */
    useEffect(() => {
        if (open) {
            ToastService.mainAPIRedZoneWarning();
        }
        if (!operatorAccessLevels.packages.lookup.editProductPartNo) {
            close();
        }
    }, [open, close, operatorAccessLevels.packages.lookup.editProductPartNo]);

    /**
     * Updates the part number of the product.
     * @type {(function(*): Promise<void>)|*}
     */
    const updatePartNo = useCallback(async (values) => {
        const [sendEmail, confirmedToContinue] = await new Promise((resolve) => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                open: true,
                title: 'Update Part Number Notice',
                description: 'Please confirm that you have informed our support team of your intent to update the this Product\'s Part Number and received their confirmation to proceed? This' +
                    ' ensures that they will change the internal system as well.',
                cancelButtonText: 'No, Send Email',
                confirmButtonText: 'Yes, Proceed',
                onClose: async (confirmed, reason) => {
                    resolve([reason === 'closeClick', reason === 'confirmClick']);
                    return true;
                }
            }));
        })
        if (!sendEmail && !confirmedToContinue) {
            return;
        }
        if (sendEmail) {
            const operatorName = operatorInformation?.name ?? "";
            const partnerName = partnerInformation?.name ?? "";

            const subject = encodeURIComponent(`Update Part Number Request for ${partNo} - ${partnerName}`);
            const body = encodeURIComponent(
                `Dear BizKey Tech Support Team,\n\n` +
                `I want to change the part number of my product from ${partNo ?? 'N/A'} to ${values.partNo}. Here are the necessary details to proceed with this operation:\n` +
                `Current Part Number: ${partNo ?? 'N/A'}\n` +
                `New Part Number: ${values.partNo}\n` +
                `Partner: ${partnerName}\n` +
                `Requesting Operator: ${operatorName}\n\n` +
                `Please proceed and inform me once the change is done to update the part number using the system.`
            );
            const mailto = `mailto:support@bizkeytech.com?cc=info@bizkeytech.com,parsa@bizkeytech.com,amir@bizkeytech.com&subject=${subject}&body=${body}`;
            const link = document.createElement('a');
            link.href = mailto;
            link.target = '_blank';
            link.click();
            return;
        }
        if (confirmedToContinue) {
            setSubmitting(true);
            const forApi = {id: partId, NewPartNumber: values.partNo, prevPartNumber: partNo}
            const response = await Api.updatePartNumber(forApi);
            if (!isMounted())
                return;
            setSubmitting(false);
            if (response?.resultFlag) {
                onSuccess(values.partNo);
                // if we want, we can pass true as an ARG and refresh the part information fetching.
                close(true);
            }
        }
    }, [close, dispatch, isMounted, onSuccess, operatorInformation?.name, partId, partNo, partnerInformation?.name]);

    return (
        <Dialog
            open={open}
            setOpen={() => close()}
            maxWidth={'sm'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            <DialogTitle id={'scroll-dialog-title'}>
                Edit {partNo ?? "Product"}'s Part Number
            </DialogTitle>
            <DialogContent dividers className={'py-5'}>
                <Form
                    onSubmit={updatePartNo}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{
                        id: 'edit-product-part-number-form',
                    }}
                >
                    {({form}) => (
                        <MuiRFF.TextField
                            name={'partNo'}
                            label={'Part Number'}
                            fullWidth
                            required={required.height}
                            disabled={submitting}
                            onChange={(e) => {
                                const partNo = e.target?.value?.toUpperCase();
                                const cursorPosition = e.target?.selectionStart;
                                e.target.value = partNo;
                                e.target.setSelectionRange(cursorPosition ?? null, cursorPosition ?? null);
                                form.change(e.target.name, partNo)
                            }}
                        />
                    )}
                </Form>
            </DialogContent>
            <DialogActions className={'d-flex align-items-center justify-content-between p-0'}>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'button'}
                    onClick={() => close()}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'submit'}
                    form={'edit-product-part-number-form'}
                    borderRadius={'8px 0 8px 0'}
                >
                    {
                        submitting
                            ? 'Updating...'
                            : "Update"
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditProductPartNumberDialog;
